import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react'
import { useNavShell } from '../contexts/NavShellContext'
import {
  View,
  Text,
  StyleSheet,
  FlatList,
  TouchableOpacity,
  Image,
  RefreshControl,
  TextInput,
  Alert,
  Platform,
  useWindowDimensions } from 'react-native'
import {
  ArrowLeft,
  Package,
  Camera,
  X,
  Check,
  Trash2,
  Tag,
  Archive,
  CheckSquare,
  Square,
  ChevronDown,
  Pencil,
  ImagePlus,
  Printer,
  Receipt,
  SlidersHorizontal,
  ArrowUpDown,
  ArrowDownAZ,
  TrendingUp,
  TrendingDown } from 'lucide-react-native'
import { useAuth } from '../contexts/AuthContext'
import { useLanguage } from '@fliisi/i18n'
import { useToast } from '../components/Toast'
import { supabase } from '../lib/supabase'
import { NativeStackNavigationProp } from '@react-navigation/native-stack'
import { RouteProp, useFocusEffect } from '@react-navigation/native'
import { RootStackParamList } from '../types/navigation'
import theme from '../theme'
import {
  Button,
  ButtonText,
  Card,
  EmptyState,
  Input,
  Spinner,
  TopBar,
  XStack,
  YStack } from '@fliisi/pollen'

const merchantColors = {
  primary: '#C07A5F',
  primaryLight: '#D4A088',
  accent: '#748C70' }

interface Props {
  navigation: NativeStackNavigationProp<RootStackParamList, 'StoreInventory'>
  route: RouteProp<RootStackParamList, 'StoreInventory'>
}

interface Product {
  id: string
  title: string
  title_en: string | null
  title_fi: string | null
  description: string | null
  description_fi: string | null
  description_en: string | null
  price: number
  status: string
  ownership_type: string
  barcode: string | null
  created_at: string
  product_images: { image_url: string }[]
}

interface Store {
  id: string
  name: string
  store_type: string
}

type FilterType = 'all' | 'for_sale' | 'draft'
type OwnershipFilter = 'all' | 'store_owned' | 'consignment'
type SortType = 'newest' | 'oldest' | 'alpha' | 'price_asc' | 'price_desc'

const CONSIGNMENT_STORE_TYPES = ['consignment_store', 'full_service_flea_market', 'self_service_flea_market']

export default function StoreInventoryScreen({ navigation, route }: Props) {
  const { storeId } = route.params
  const { user } = useAuth()
  const { t, language } = useLanguage()
  const toast = useToast()
  const { width } = useWindowDimensions()
  const [store, setStore] = useState<Store | null>(null)
  const [products, setProducts] = useState<Product[]>([])
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState<FilterType>('all')
  const [sortBy, setSortBy] = useState<SortType>('newest')
  const [ownershipFilter, setOwnershipFilter] = useState<OwnershipFilter>('all')
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false)

  // Selection / bulk mode
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [bulkMode, setBulkMode] = useState(false)
  const { setHidden } = useNavShell()

  // Hide bottom nav in bulk mode
  useEffect(() => {
    setHidden(bulkMode)
    return () => setHidden(false)
  }, [bulkMode])

  // Inline editing
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editTitle, setEditTitle] = useState('')
  const [editDescription, setEditDescription] = useState('')
  const [editPrice, setEditPrice] = useState('')
  const [saving, setSaving] = useState(false)

  useFocusEffect(
    useCallback(() => {
      fetchStore()
      fetchProducts()

      // Realtime: show new products the moment they're created + update titles when AI is done
      const channel = supabase
        .channel(`inventory-${storeId}`)
        .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'products', filter: `flea_market_id=eq.${storeId}` },
          async (payload) => {
            const { data } = await supabase
              .from('products')
              .select('id, title, title_en, title_fi, description, description_fi, description_en, price, status, ownership_type, barcode, created_at, product_images(image_url)')
              .eq('id', (payload.new as any).id)
              .single()
            if (data) setProducts((prev) => [data as Product, ...prev])
          })
        .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'products', filter: `flea_market_id=eq.${storeId}` },
          (payload) => {
            setProducts((prev) =>
              prev.map((p) => p.id === (payload.new as any).id ? { ...p, ...payload.new } : p)
            )
          })
        .subscribe()

      return () => { supabase.removeChannel(channel) }
    }, [storeId])
  )

  const fetchStore = async () => {
    try {
      const { data, error } = await supabase
        .from('flea_markets')
        .select('id, name, store_type')
        .eq('id', storeId)
        .single()
      if (error) throw error
      setStore(data)
    } catch (error) {
      console.error('Error fetching store:', error)
    }
  }

  const fetchProducts = async () => {
    setLoading(true)
    try {
      const { data, error } = await supabase
        .from('products')
        .select(`
          id, title, title_en, title_fi,
          description, description_fi, description_en,
          price, status, ownership_type, barcode, created_at,
          product_images (image_url)
        `)
        .eq('flea_market_id', storeId)
        .neq('status', 'sold')
        .order('created_at', { ascending: false })

      if (error) throw error
      setProducts(data || [])
    } catch (error) {
      console.error('Error fetching products:', error)
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }

  const onRefresh = useCallback(() => {
    setRefreshing(true)
    fetchProducts()
  }, [storeId])

  const getTitle = (p: Product) => {
    if (language === 'fi' && p.title_fi) return p.title_fi
    if (language === 'en' && p.title_en) return p.title_en
    return p.title || t('untitled') || 'Untitled'
  }

  const getDescription = (p: Product) => {
    if (language === 'fi' && p.description_fi) return p.description_fi
    if (language === 'en' && p.description_en) return p.description_en
    return p.description || ''
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'for_sale': return merchantColors.accent
      case 'sold': return '#3B82F6'
      case 'draft': return '#F59E0B'
      case 'reserved': return '#8B5CF6'
      default: return '#999'
    }
  }

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'for_sale': return t('for_sale') || 'For sale'
      case 'sold': return t('sold') || 'Sold'
      case 'draft': return t('draft') || 'Draft'
      case 'reserved': return t('reserved') || 'Reserved'
      default: return status
    }
  }

  // --- Inline edit ---
  const startEditing = (p: Product) => {
    setEditingId(p.id)
    setEditTitle(getTitle(p))
    setEditDescription(getDescription(p))
    setEditPrice(p.price?.toString() || '0')
  }

  const cancelEditing = () => {
    setEditingId(null)
  }

  const saveEdit = async () => {
    if (!editingId) return
    setSaving(true)
    try {
      const updates: Record<string, any> = {
        price: parseFloat(editPrice) || 0 }
      // Save to the language-specific field
      if (language === 'fi') {
        updates.title_fi = editTitle
        updates.description_fi = editDescription
      } else {
        updates.title_en = editTitle
        updates.description_en = editDescription
      }
      // Always update the base title too
      updates.title = editTitle

      const { error } = await supabase
        .from('products')
        .update(updates)
        .eq('id', editingId)

      if (error) throw error

      // Update local state
      setProducts((prev) =>
        prev.map((p) =>
          p.id === editingId
            ? { ...p, ...updates }
            : p
        )
      )
      toast.success(t('saved') || 'Saved', '')
      setEditingId(null)
    } catch (error: any) {
      toast.error(t('error') || 'Error', error.message)
    } finally {
      setSaving(false)
    }
  }

  // --- Bulk operations ---
  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const selectAll = () => {
    setSelectedIds(new Set(filteredProducts.map((p) => p.id)))
  }

  const deselectAll = () => {
    setSelectedIds(new Set())
  }

  const exitBulkMode = () => {
    setBulkMode(false)
    setSelectedIds(new Set())
  }

  // Generate a barcode matching the DB format: STR + YYYYMMDDHHMMSS + 4-digit random
  const generateBarcode = () => {
    const now = new Date()
    const ts = now.getFullYear().toString() +
      String(now.getMonth() + 1).padStart(2, '0') +
      String(now.getDate()).padStart(2, '0') +
      String(now.getHours()).padStart(2, '0') +
      String(now.getMinutes()).padStart(2, '0') +
      String(now.getSeconds()).padStart(2, '0')
    const rand = String(Math.floor(Math.random() * 10000)).padStart(4, '0')
    return `STR${ts}${rand}`
  }

  const bulkSetStatus = async (status: string) => {
    if (selectedIds.size === 0) return
    const ids = Array.from(selectedIds)
    try {
      // For statuses that require a barcode, generate one for products that lack it
      const needsBarcode = ['for_sale', 'reserved', 'sold'].includes(status)

      if (needsBarcode) {
        const missingBarcode = products.filter(
          (p) => ids.includes(p.id) && !p.barcode
        )
        // Update each product that needs a barcode individually (barcodes must be unique)
        for (const p of missingBarcode) {
          const barcode = generateBarcode()
          const { error } = await supabase
            .from('products')
            .update({ barcode })
            .eq('id', p.id)
          if (error) throw error
          // Update local state
          setProducts((prev) =>
            prev.map((pr) => pr.id === p.id ? { ...pr, barcode } : pr)
          )
        }
      }

      const { error } = await supabase
        .from('products')
        .update({ status })
        .in('id', ids)

      if (error) throw error

      setProducts((prev) =>
        prev.map((p) => (ids.includes(p.id) ? { ...p, status } : p))
      )
      toast.success(
        `${ids.length} ${t('products') || 'products'}`,
        `→ ${getStatusLabel(status)}`
      )
      exitBulkMode()
    } catch (error: any) {
      toast.error(t('error') || 'Error', error.message)
    }
  }

  const bulkDelete = async () => {
    if (selectedIds.size === 0) return
    const ids = Array.from(selectedIds)

    const doDelete = async () => {
      try {
        const { error } = await supabase
          .from('products')
          .delete()
          .in('id', ids)

        if (error) throw error

        setProducts((prev) => prev.filter((p) => !ids.includes(p.id)))
        toast.success(
          `${ids.length} ${t('products') || 'products'}`,
          t('deleted') || 'Deleted'
        )
        exitBulkMode()
      } catch (error: any) {
        toast.error(t('error') || 'Error', error.message)
      }
    }

    if (Platform.OS === 'web') {
      if (window.confirm(`${t('delete') || 'Delete'} ${ids.length} ${t('products') || 'products'}?`)) {
        doDelete()
      }
    } else {
      Alert.alert(
        t('delete') || 'Delete',
        `${t('delete') || 'Delete'} ${ids.length} ${t('products') || 'products'}?`,
        [
          { text: t('cancel') || 'Cancel', style: 'cancel' },
          { text: t('delete') || 'Delete', style: 'destructive', onPress: doDelete },
        ]
      )
    }
  }

  const isConsignmentStore = store && CONSIGNMENT_STORE_TYPES.includes(store.store_type)

  const filteredProducts = useMemo(() => {
    let result = products.filter((p) => {
      if (search) {
        const s = search.toLowerCase()
        const matchTitle = getTitle(p).toLowerCase().includes(s)
        const matchBarcode = p.barcode?.toLowerCase().includes(s)
        if (!matchTitle && !matchBarcode) return false
      }
      // Status filter
      if (filter === 'for_sale' && p.status !== 'for_sale') return false
      if (filter === 'draft' && p.status !== 'draft') return false
      // Ownership filter
      if (ownershipFilter === 'store_owned' && p.ownership_type !== 'store_owned') return false
      if (ownershipFilter === 'consignment' && p.ownership_type !== 'consignment') return false
      return true
    })

    return [...result].sort((a, b) => {
      switch (sortBy) {
        case 'newest': return new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        case 'oldest': return new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
        case 'alpha': return getTitle(a).localeCompare(getTitle(b), 'fi')
        case 'price_asc': return (a.price ?? 0) - (b.price ?? 0)
        case 'price_desc': return (b.price ?? 0) - (a.price ?? 0)
        default: return 0
      }
    })
  }, [products, search, filter, sortBy, ownershipFilter, language])

  const stats = useMemo(() => ({
    total: products.length,
    for_sale: products.filter((p) => p.status === 'for_sale').length,
    draft: products.filter((p) => p.status === 'draft').length }), [products])

  // --- Render list item ---
  const renderProduct = ({ item }: { item: Product }) => {
    const isEditing = editingId === item.id
    const isSelected = selectedIds.has(item.id)
    const imageUrl = item.product_images?.[0]?.image_url

    if (isEditing) {
      return (
        <View style={styles.editRow}>
          {/* Thumbnail */}
          {imageUrl ? (
            <Image source={{ uri: imageUrl }} style={styles.editThumb} />
          ) : (
            <View style={[styles.editThumb, styles.noImage]}>
              <Package size={16} color="#bbb" />
            </View>
          )}

          <View style={styles.editFields}>
            <TextInput
              style={styles.editInput}
              value={editTitle}
              onChangeText={setEditTitle}
              placeholder={t('title') || 'Title'}
              placeholderTextColor="#aaa"
              autoFocus
            />
            <TextInput
              style={[styles.editInput, styles.editDescInput]}
              value={editDescription}
              onChangeText={setEditDescription}
              placeholder={t('description') || 'Description'}
              placeholderTextColor="#aaa"
              multiline
              numberOfLines={2}
            />
            <View style={styles.editPriceRow}>
              <Text style={styles.eurSign}>€</Text>
              <TextInput
                style={[styles.editInput, styles.editPriceInput]}
                value={editPrice}
                onChangeText={setEditPrice}
                keyboardType="decimal-pad"
                placeholder="0.00"
                placeholderTextColor="#aaa"
              />
            </View>
          </View>

          <View style={styles.editActions}>
            <TouchableOpacity style={styles.editSaveBtn} onPress={saveEdit} disabled={saving}>
              {saving ? (
                <Spinner size="small" />
              ) : (
                <Check size={18} color="#fff" />
              )}
            </TouchableOpacity>
            <TouchableOpacity style={styles.editCancelBtn} onPress={cancelEditing}>
              <X size={18} color={merchantColors.primary} />
            </TouchableOpacity>
          </View>
        </View>
      )
    }

    return (
      <TouchableOpacity
        style={[styles.row, isSelected && styles.rowSelected]}
        onPress={() => {
          if (bulkMode) {
            toggleSelect(item.id)
          } else {
            navigation.navigate('ProductDetail', { productId: item.id })
          }
        }}
        onLongPress={() => {
          if (!bulkMode) {
            setBulkMode(true)
            setSelectedIds(new Set([item.id]))
          }
        }}
      >
        {/* Checkbox (bulk mode) */}
        {bulkMode && (
          <TouchableOpacity
            style={styles.checkboxArea}
            onPress={() => toggleSelect(item.id)}
          >
            {isSelected ? (
              <CheckSquare size={22} color={merchantColors.primary} />
            ) : (
              <Square size={22} color="#ccc" />
            )}
          </TouchableOpacity>
        )}

        {/* Thumbnail */}
        {imageUrl ? (
          <Image source={{ uri: imageUrl }} style={styles.thumb} />
        ) : (
          <View style={[styles.thumb, styles.noImage]}>
            <Package size={18} color="#ccc" />
          </View>
        )}

        {/* Info */}
        <View style={styles.info}>
          {item.title === '•••' && !item.title_fi && !item.title_en ? (
            <View style={styles.labelingRow}>
              <Spinner size="small" />
              <Text style={styles.labelingText}>AI labeling...</Text>
            </View>
          ) : (
            <>
              <Text style={styles.title} numberOfLines={1}>{getTitle(item)}</Text>
              {getDescription(item) ? (
                <Text style={styles.desc} numberOfLines={1}>{getDescription(item)}</Text>
              ) : (
                <Text style={styles.descEmpty}>{t('no_description') || 'No description'}</Text>
              )}
            </>
          )}
          <View style={styles.meta}>
            <View style={[styles.statusDot, { backgroundColor: getStatusColor(item.status) }]} />
            <Text style={styles.statusText}>{getStatusLabel(item.status)}</Text>
            {item.barcode && (
              <Text style={styles.barcode}>#{item.barcode}</Text>
            )}
          </View>
        </View>

        {/* Price */}
        <Text style={styles.price}>€{item.price?.toFixed(2) || '0.00'}</Text>

        {/* Edit button */}
        {!bulkMode && (
          <TouchableOpacity
            style={styles.editBtn}
            onPress={() => startEditing(item)}
            hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
          >
            <Pencil size={16} color={merchantColors.primary} />
          </TouchableOpacity>
        )}
      </TouchableOpacity>
    )
  }

  const FilterChip = ({ value, label, count }: { value: FilterType; label: string; count?: number }) => (
    <TouchableOpacity
      style={[styles.filterChip, filter === value && styles.filterChipActive]}
      onPress={() => setFilter(value)}
    >
      <Text style={[styles.filterChipText, filter === value && styles.filterChipTextActive]}>
        {label}
        {count !== undefined && ` (${count})`}
      </Text>
    </TouchableOpacity>
  )

  return (
    <View style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        {bulkMode ? (
          <>
            <TouchableOpacity onPress={exitBulkMode} style={styles.headerBtn}>
              <X size={20} color={merchantColors.primary} />
            </TouchableOpacity>
            <Text style={styles.headerTitle}>
              {selectedIds.size} {t('selected') || 'selected'}
            </Text>
            <TouchableOpacity
              onPress={selectedIds.size === filteredProducts.length ? deselectAll : selectAll}
              style={styles.headerBtn}
            >
              <Text style={styles.selectAllText}>
                {selectedIds.size === filteredProducts.length
                  ? (t('deselect_all') || 'Deselect')
                  : (t('select_all') || 'Select all')}
              </Text>
            </TouchableOpacity>
          </>
        ) : (
          <>
            <TouchableOpacity onPress={() => navigation.goBack()} style={styles.headerBtn}>
              <ArrowLeft size={20} color={merchantColors.primary} />
            </TouchableOpacity>
            <View style={styles.headerCenter}>
              <Text style={styles.headerTitle}>{t('inventory') || 'Inventory'}</Text>
              <Text style={styles.headerCount}>{stats.total} {t('products') || 'products'}</Text>
            </View>
            <View style={styles.headerBtnRow}>
              <TouchableOpacity
                style={styles.headerBtn}
                onPress={() => navigation.navigate('StoreTransactions', { storeId })}
              >
                <Receipt size={20} color={merchantColors.primary} />
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.headerBtn}
                onPress={() => navigation.navigate('StorePrintTags', { storeId })}
              >
                <Printer size={20} color={merchantColors.primary} />
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.headerBtn}
                onPress={() => setBulkMode(true)}
              >
                <CheckSquare size={20} color={merchantColors.primary} />
              </TouchableOpacity>
            </View>
          </>
        )}
      </View>

      {/* Search */}
      <View style={styles.searchBar}>
        <TextInput
          style={styles.searchInput}
          value={search}
          onChangeText={setSearch}
          placeholder={t('search_products') || 'Search by title or barcode...'}
          placeholderTextColor="#aaa"
        />
        {search.length > 0 && (
          <TouchableOpacity style={styles.clearSearch} onPress={() => setSearch('')}>
            <X size={16} color="#999" />
          </TouchableOpacity>
        )}
      </View>

      {/* Status filter chips + advanced filter toggle */}
      <View style={styles.filtersRow}>
        <View style={styles.filters}>
          <FilterChip value="all" label={t('all') || 'All'} count={stats.total} />
          <FilterChip value="for_sale" label={t('for_sale') || 'For sale'} count={stats.for_sale} />
          <FilterChip value="draft" label={t('draft') || 'Draft'} count={stats.draft} />
        </View>
        <TouchableOpacity
          style={[styles.filterToggleBtn, showAdvancedFilters && styles.filterToggleBtnActive]}
          onPress={() => setShowAdvancedFilters(v => !v)}
        >
          <SlidersHorizontal size={16} color={showAdvancedFilters ? '#fff' : merchantColors.primary} />
          {(sortBy !== 'newest' || ownershipFilter !== 'all') && (
            <View style={styles.filterActiveDot} />
          )}
        </TouchableOpacity>
      </View>

      {/* Advanced filter panel */}
      {showAdvancedFilters && (
        <View style={styles.advancedFilters}>
          {/* Sort */}
          <Text style={styles.advancedFilterLabel}>{t('sort_by') || 'Sort by'}</Text>
          <View style={styles.advancedFilterRow}>
            {([
              { key: 'newest', label: t('newest') || 'Newest', Icon: ArrowUpDown },
              { key: 'oldest', label: t('oldest') || 'Oldest', Icon: ArrowUpDown },
              { key: 'alpha', label: 'A–Z', Icon: ArrowDownAZ },
              { key: 'price_asc', label: t('price_asc') || 'Price ↑', Icon: TrendingUp },
              { key: 'price_desc', label: t('price_desc') || 'Price ↓', Icon: TrendingDown },
            ] as { key: SortType; label: string; Icon: any }[]).map(({ key, label, Icon }) => (
              <TouchableOpacity
                key={key}
                style={[styles.advancedChip, sortBy === key && styles.advancedChipActive]}
                onPress={() => setSortBy(key)}
              >
                <Icon size={12} color={sortBy === key ? '#fff' : merchantColors.primary} />
                <Text style={[styles.advancedChipText, sortBy === key && styles.advancedChipTextActive]}>
                  {label}
                </Text>
              </TouchableOpacity>
            ))}
          </View>

          {/* Ownership (consignment stores only) */}
          {isConsignmentStore && (
            <>
              <Text style={styles.advancedFilterLabel}>{t('ownership') || 'Ownership'}</Text>
              <View style={styles.advancedFilterRow}>
                {([
                  { key: 'all', label: t('all') || 'All' },
                  { key: 'store_owned', label: t('store_owned') || 'Store' },
                  { key: 'consignment', label: t('consignment') || 'Consignment' },
                ] as { key: OwnershipFilter; label: string }[]).map(({ key, label }) => (
                  <TouchableOpacity
                    key={key}
                    style={[styles.advancedChip, ownershipFilter === key && styles.advancedChipActive]}
                    onPress={() => setOwnershipFilter(key)}
                  >
                    <Text style={[styles.advancedChipText, ownershipFilter === key && styles.advancedChipTextActive]}>
                      {label}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            </>
          )}
        </View>
      )}

      {/* Product list */}
      {loading ? (
        <YStack f={1} jc="center" ai="center"><Spinner size="large" /></YStack>
      ) : (
        <FlatList
          data={filteredProducts}
          renderItem={renderProduct}
          keyExtractor={(item) => item.id}
          contentContainerStyle={styles.listContent}
          refreshControl={
            <RefreshControl refreshing={refreshing} onRefresh={onRefresh} colors={[merchantColors.primary]} />
          }
          ListEmptyComponent={
            <View style={styles.emptyState}>
              <Package size={48} color="#ddd" />
              <Text style={styles.emptyText}>
                {search ? (t('no_results') || 'No products found') : (t('no_products') || 'No products yet')}
              </Text>
              <Text style={styles.emptyHint}>
                {t('add_product_hint') || 'Tap the camera button to add products'}
              </Text>
            </View>
          }
        />
      )}

      {/* Bulk action bar */}
      {bulkMode && selectedIds.size > 0 && (
        <View style={styles.bulkBar}>
          <TouchableOpacity style={styles.bulkBtn} onPress={() => bulkSetStatus('for_sale')}>
            <Tag size={18} color={merchantColors.accent} />
            <Text style={[styles.bulkBtnText, { color: merchantColors.accent }]}>{t('publish') || 'Publish'}</Text>
          </TouchableOpacity>
          <TouchableOpacity style={styles.bulkBtn} onPress={() => bulkSetStatus('draft')}>
            <Archive size={18} color="#F59E0B" />
            <Text style={[styles.bulkBtnText, { color: '#F59E0B' }]}>{t('draft') || 'Draft'}</Text>
          </TouchableOpacity>
          <TouchableOpacity style={styles.bulkBtn} onPress={() => bulkSetStatus('sold')}>
            <Check size={18} color="#3B82F6" />
            <Text style={[styles.bulkBtnText, { color: '#3B82F6' }]}>{t('sold') || 'Sold'}</Text>
          </TouchableOpacity>
          <TouchableOpacity style={styles.bulkBtn} onPress={bulkDelete}>
            <Trash2 size={18} color="#EF4444" />
            <Text style={[styles.bulkBtnText, { color: '#EF4444' }]}>{t('delete') || 'Delete'}</Text>
          </TouchableOpacity>
        </View>
      )}

      {/* FABs — camera + gallery */}
      {!bulkMode && (
        <View style={styles.fabContainer}>
          <TouchableOpacity
            style={styles.fabSecondary}
            onPress={() => navigation.navigate('StoreCamera', { storeId, pickFromGallery: true })}
          >
            <ImagePlus size={22} color={merchantColors.primary} />
          </TouchableOpacity>
          <TouchableOpacity
            style={styles.fab}
            onPress={() => navigation.navigate('StoreCamera', { storeId })}
          >
            <Camera size={26} color="#fff" />
          </TouchableOpacity>
        </View>
      )}

    </View>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f8f8f8' },
  // Header
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingTop: 16,
    paddingBottom: 12,
    backgroundColor: '#fff',
    borderBottomWidth: 1,
    borderBottomColor: '#f0f0f0' },
  headerBtn: {
    padding: 4,
    minWidth: 36 },
  headerBtnRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4 },
  headerCenter: {
    alignItems: 'center',
    flex: 1 },
  headerTitle: {
    fontSize: 17,
    fontWeight: '700',
    color: '#1a1a1a' },
  headerCount: {
    fontSize: 12,
    color: '#999',
    marginTop: 1 },
  selectAllText: {
    fontSize: 14,
    fontWeight: '600',
    color: merchantColors.primary },
  // Search
  searchBar: {
    backgroundColor: '#fff',
    paddingHorizontal: 16,
    paddingVertical: 8,
    flexDirection: 'row',
    alignItems: 'center' },
  searchInput: {
    flex: 1,
    backgroundColor: '#f4f4f4',
    borderRadius: 10,
    paddingHorizontal: 14,
    paddingVertical: 9,
    fontSize: 15,
    color: '#1a1a1a' },
  clearSearch: {
    position: 'absolute',
    right: 26,
    padding: 6 },
  // Filters
  filtersRow: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#fff',
    borderBottomWidth: 1,
    borderBottomColor: '#f0f0f0' },
  filters: {
    flex: 1,
    flexDirection: 'row',
    paddingHorizontal: 16,
    paddingVertical: 8,
    gap: 6,
    flexWrap: 'wrap' },
  filterToggleBtn: {
    width: 36,
    height: 36,
    borderRadius: 18,
    marginRight: 12,
    backgroundColor: '#f4f4f4',
    justifyContent: 'center',
    alignItems: 'center' },
  filterToggleBtnActive: {
    backgroundColor: merchantColors.primary },
  filterActiveDot: {
    position: 'absolute',
    top: 6,
    right: 6,
    width: 7,
    height: 7,
    borderRadius: 4,
    backgroundColor: '#F59E0B' },
  advancedFilters: {
    backgroundColor: '#faf8f6',
    paddingHorizontal: 16,
    paddingTop: 10,
    paddingBottom: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#f0f0f0',
    gap: 6 },
  advancedFilterLabel: {
    fontSize: 11,
    fontWeight: '600',
    color: '#999',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginTop: 4 },
  advancedFilterRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
    marginTop: 4 },
  advancedChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 16,
    backgroundColor: '#ede8e3',
    borderWidth: 1,
    borderColor: 'transparent' },
  advancedChipActive: {
    backgroundColor: merchantColors.primary,
    borderColor: merchantColors.primary },
  advancedChipText: {
    fontSize: 12,
    fontWeight: '500',
    color: merchantColors.primary },
  advancedChipTextActive: {
    color: '#fff' },
  filterChip: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 20,
    backgroundColor: '#f4f4f4' },
  filterChipActive: {
    backgroundColor: merchantColors.primary },
  filterChipText: {
    fontSize: 12,
    color: '#777',
    fontWeight: '600' },
  filterChipTextActive: {
    color: '#fff' },
  // List
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center' },
  listContent: {
    paddingBottom: 160 },
  // Row
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 10,
    backgroundColor: '#fff',
    borderBottomWidth: 1,
    borderBottomColor: '#f5f5f5',
    gap: 12 },
  rowSelected: {
    backgroundColor: `${merchantColors.primary}08` },
  checkboxArea: {
    paddingRight: 4 },
  thumb: {
    width: 52,
    height: 52,
    borderRadius: 8,
    backgroundColor: '#f4f4f4' },
  noImage: {
    justifyContent: 'center',
    alignItems: 'center' },
  info: {
    flex: 1,
    minWidth: 0 },
  title: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1a1a1a',
    marginBottom: 2 },
  desc: {
    fontSize: 12,
    color: '#777',
    marginBottom: 4 },
  labelingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginBottom: 4 },
  labelingText: {
    fontSize: 12,
    color: '#aaa',
    fontStyle: 'italic' },
  descEmpty: {
    fontSize: 12,
    color: '#bbb',
    fontStyle: 'italic',
    marginBottom: 4 },
  meta: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6 },
  statusDot: {
    width: 7,
    height: 7,
    borderRadius: 4 },
  statusText: {
    fontSize: 11,
    color: '#999',
    fontWeight: '500' },
  barcode: {
    fontSize: 11,
    color: '#bbb',
    marginLeft: 4 },
  price: {
    fontSize: 15,
    fontWeight: '700',
    color: '#1a1a1a',
    minWidth: 60,
    textAlign: 'right' },
  editBtn: {
    padding: 6 },
  // Inline edit row
  editRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    paddingHorizontal: 16,
    paddingVertical: 10,
    backgroundColor: '#FFFBF8',
    borderBottomWidth: 1,
    borderBottomColor: merchantColors.primaryLight,
    borderLeftWidth: 3,
    borderLeftColor: merchantColors.primary,
    gap: 12 },
  editThumb: {
    width: 52,
    height: 52,
    borderRadius: 8,
    backgroundColor: '#f4f4f4',
    marginTop: 2 },
  editFields: {
    flex: 1,
    gap: 6 },
  editInput: {
    backgroundColor: '#fff',
    borderWidth: 1,
    borderColor: '#e0e0e0',
    borderRadius: 8,
    paddingHorizontal: 10,
    paddingVertical: 7,
    fontSize: 14,
    color: '#1a1a1a' },
  editDescInput: {
    minHeight: 44,
    textAlignVertical: 'top' },
  editPriceRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6 },
  eurSign: {
    fontSize: 15,
    fontWeight: '700',
    color: '#1a1a1a' },
  editPriceInput: {
    width: 100,
    fontWeight: '700' },
  editActions: {
    gap: 6,
    marginTop: 2 },
  editSaveBtn: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: merchantColors.accent,
    justifyContent: 'center',
    alignItems: 'center' },
  editCancelBtn: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: `${merchantColors.primary}15`,
    justifyContent: 'center',
    alignItems: 'center' },
  // Bulk action bar
  bulkBar: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 16,
    paddingBottom: 28,
    backgroundColor: '#fff',
    borderTopWidth: 1,
    borderTopColor: '#f0f0f0',
    ...(Platform.OS === 'web' ? { boxShadow: '0 -2px 12px rgba(0,0,0,0.08)' } : {
      shadowColor: '#000',
      shadowOffset: { width: 0, height: -2 },
      shadowOpacity: 0.08,
      shadowRadius: 12,
      elevation: 8 }) },
  bulkBtn: {
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 12,
    paddingVertical: 8 },
  bulkBtnText: {
    fontSize: 11,
    fontWeight: '700' },
  // FABs
  fabContainer: {
    position: 'absolute',
    right: 16,
    bottom: 90,
    alignItems: 'center',
    gap: 10 },
  fabSecondary: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: '#fff',
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 1.5,
    borderColor: merchantColors.primary,
    ...(Platform.OS === 'web' ? { boxShadow: '0 2px 8px rgba(0,0,0,0.12)' } : {
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.12,
      shadowRadius: 8,
      elevation: 4 }) },
  fab: {
    width: 56,
    height: 56,
    borderRadius: 28,
    backgroundColor: merchantColors.primary,
    justifyContent: 'center',
    alignItems: 'center',
    ...(Platform.OS === 'web' ? { boxShadow: '0 4px 12px rgba(192,122,95,0.4)' } : {
      shadowColor: merchantColors.primary,
      shadowOffset: { width: 0, height: 4 },
      shadowOpacity: 0.4,
      shadowRadius: 12,
      elevation: 8 }) },
  // Empty state
  emptyState: {
    alignItems: 'center',
    paddingVertical: 60,
    paddingHorizontal: 32 },
  emptyText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#555',
    marginTop: 16,
    marginBottom: 8 },
  emptyHint: {
    fontSize: 14,
    color: '#999',
    textAlign: 'center' } })